import { Lightbulb, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader';
import { useAuth } from '../auth/auth-context';

import { type HelpBlock, type HelpSection, helpFor } from './help-content';

function blockText(b: HelpBlock): string {
  if (b.type === 'p' || b.type === 'tip') return b.text;
  if (b.type === 'image') return b.caption;
  if (b.type === 'steps') return b.items.join(' ');
  return b.items.map((i) => (typeof i === 'string' ? i : `${i.term} ${i.text}`)).join(' ');
}

function matches(section: HelpSection, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [section.title, ...section.blocks.map(blockText)].join(' ').toLowerCase();
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

function Block({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'p':
      return <p className="max-w-[68ch] text-sm leading-relaxed text-slate-700">{block.text}</p>;
    case 'steps':
      return (
        <ol className="max-w-[68ch] space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
              <span
                className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    case 'list':
      return (
        <ul className="max-w-[68ch] space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-700">
              <span
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400"
                aria-hidden="true"
              />
              {typeof item === 'string' ? (
                <span>{item}</span>
              ) : (
                <span>
                  <strong className="font-semibold text-slate-900">{item.term}</strong> :{' '}
                  {item.text}
                </span>
              )}
            </li>
          ))}
        </ul>
      );
    case 'image':
      return (
        <figure className="space-y-2">
          <a
            href={`/aide/${block.shot}.png`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-fit max-w-full overflow-hidden rounded-lg border border-slate-200 shadow-sm transition hover:shadow-md"
            title="Ouvrir la capture en grand"
          >
            <img
              src={`/aide/${block.shot}.png`}
              alt={block.alt}
              width={block.width}
              height={block.height}
              loading="lazy"
              className="block h-auto max-w-full"
            />
          </a>
          <figcaption className="max-w-[68ch] text-xs text-slate-500">{block.caption}</figcaption>
        </figure>
      );
    case 'tip':
      return (
        <p className="flex max-w-[68ch] gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-900">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{block.text}</span>
        </p>
      );
  }
}

/**
 * Aide intégrée : mode d'emploi filtré selon le rôle connecté.
 * Les liens « Aide » des en-têtes de page pointent vers `/aide#<section>`.
 */
export function HelpPage() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const [query, setQuery] = useState('');
  const groups = useMemo(() => (user ? helpFor(user.role) : []), [user]);
  const visible = groups
    .map((g) => ({ ...g, sections: g.sections.filter((s) => matches(s, query)) }))
    .filter((g) => g.sections.length > 0);

  // Arrivée depuis un lien contextuel : amène la section demandée en vue.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <>
      <PageHeader title="Aide" subtitle="Le mode d’emploi du dashboard, adapté à votre rôle." />
      <div className="grid gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:px-8">
        <nav aria-label="Sommaire de l’aide" className="lg:sticky lg:top-4 lg:self-start">
          <label htmlFor="help-search" className="sr-only">
            Rechercher dans l’aide
          </label>
          <div className="relative mb-5">
            <Search
              className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400"
              aria-hidden="true"
            />
            <input
              id="help-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-8 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <div className="space-y-5">
            {visible.map((g) => (
              <div key={g.title}>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {g.title}
                </div>
                <ul className="space-y-0.5">
                  {g.sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="block rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-white hover:text-slate-900"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div className="min-w-0 space-y-10">
          {visible.length === 0 && (
            <p className="surface px-4 py-8 text-center text-sm text-slate-500">
              Aucune rubrique ne correspond à « {query} ». Essayez un autre mot, par exemple «
              rapport » ou « exécuter ».
            </p>
          )}
          {visible.map((g) => (
            <section key={g.title} aria-label={g.title} className="space-y-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {g.title}
              </h2>
              {g.sections.map((s) => (
                <article key={s.id} id={s.id} className="surface scroll-mt-4 space-y-3 p-5 sm:p-6">
                  <h3 className="text-base font-bold text-slate-900">{s.title}</h3>
                  {s.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </article>
              ))}
            </section>
          ))}
        </div>
      </div>
    </>
  );
}
